import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const TEMPLATES = [
  { key: 'confirm', label: '신청확인', subject: '[이음] 서비스 신청이 접수되었습니다', body: '안녕하세요, {{고객명}}님.\n\n{{서비스카테고리}} 서비스 신청이 정상적으로 접수되었습니다.\n접수번호: {{접수번호}}\n\n빠른 시일 내에 담당자가 연락드리겠습니다.\n\n감사합니다.\n이음 드림' },
  { key: 'assign', label: '배정알림', subject: '[이음] 담당 업체가 배정되었습니다', body: '안녕하세요, {{고객명}}님.\n\n요청하신 {{서비스카테고리}} 서비스에 담당 업체가 배정되었습니다.\n\n담당업체: {{업체명}}\n담당자: {{담당자명}}\n연락처: {{담당자연락처}}\n\n곧 담당자가 연락드릴 예정입니다.\n\n감사합니다.\n이음 드림' },
  { key: 'settlement', label: '정산안내', subject: '[이음] {{월}}월 정산 안내', body: '안녕하세요, {{업체명/중개사명}}님.\n\n{{월}}월 정산 내역을 안내드립니다.\n\n총 정산금액: {{금액}}원\n정산 예정일: {{정산일}}\n\n상세 내역은 마이페이지에서 확인하실 수 있습니다.\n\n감사합니다.\n이음 드림' },
];

const FormMail = () => {
  const [selectedKey, setSelectedKey] = useState('confirm');
  const [templates, setTemplates] = useState(
    TEMPLATES.reduce((acc, t) => ({ ...acc, [t.key]: { subject: t.subject, body: t.body } }), {})
  );
  const [senderEmail, setSenderEmail] = useState('no-reply@ieum.in');
  const [senderName, setSenderName] = useState('이음');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'formMail'));
        if (snap.exists()) {
          const d = snap.data();
          if (d.senderEmail) setSenderEmail(d.senderEmail);
          if (d.senderName) setSenderName(d.senderName);
          if (d.templates) setTemplates((prev) => ({ ...prev, ...d.templates }));
        }
      } catch (err) {
        console.warn('폼메일 설정 로드 실패:', err);
      }
    })();
  }, []);

  const current = templates[selectedKey];

  const updateTemplate = (field, value) => {
    setTemplates({ ...templates, [selectedKey]: { ...current, [field]: value } });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'formMail'), {
        senderEmail,
        senderName,
        templates,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      alert('폼메일 설정이 저장되었습니다.');
    } catch (err) {
      console.error('폼메일 저장 실패:', err);
      alert('저장에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Title>폼메일 설정</Title>

      <Card>
        <CardTitle>발신자 정보</CardTitle>
        <Row>
          <Field>
            <FieldLabel>발신자 이메일</FieldLabel>
            <Input value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel>발신자명</FieldLabel>
            <Input value={senderName} onChange={(e) => setSenderName(e.target.value)} />
          </Field>
        </Row>
      </Card>

      <Card>
        <CardTitle>이메일 템플릿</CardTitle>
        <Field>
          <FieldLabel>템플릿 선택</FieldLabel>
          <Select value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)}>
            {TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </Select>
        </Field>
        <Field>
          <FieldLabel>제목</FieldLabel>
          <Input value={current.subject} onChange={(e) => updateTemplate('subject', e.target.value)} />
        </Field>
        <Field>
          <FieldLabel>본문</FieldLabel>
          <Textarea rows={12} value={current.body} onChange={(e) => updateTemplate('body', e.target.value)} />
        </Field>
        <HelpText>{'{{변수명}}'} 형식으로 치환 변수를 사용할 수 있습니다.</HelpText>
      </Card>

      <SaveBar>
        <SaveBtn onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</SaveBtn>
      </SaveBar>
    </Container>
  );
};

export default FormMail;

const Container = styled.div``;
const Title = styled.h1`font-size:18px;font-weight:600;margin-bottom:20px;`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  padding: 24px;
  margin-bottom: 20px;
`;

const CardTitle = styled.h2`font-size:14px;font-weight:500;margin-bottom:16px;`;

const Row = styled.div`display:flex;gap:16px;@media(max-width:600px){flex-direction:column;}`;

const Field = styled.div`margin-bottom:16px;flex:1;`;
const FieldLabel = styled.label`display:block;font-size:13px;font-weight:500;color:${({ theme }) => theme.colors.textSecondary};margin-bottom:6px;`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const Select = styled.select`
  width: 100%;
  max-width: 300px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-family: monospace;
  line-height: 1.6;
  resize: vertical;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const HelpText = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: -8px;
`;

const SaveBar = styled.div`display:flex;justify-content:flex-end;`;
const SaveBtn = styled.button`
  padding: 10px 32px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-weight: 500;
`;
